import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Table,
  Tooltip,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { uniqueId } from "lodash";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import moment from "moment";
import { useAppSelector } from "../../../..";
import { CharacteristicValues, TableDataCharacteristic } from "./CharacteristicsTableTypes";
import { IObjectCharTypes, IObjectCharTypeValue } from "../../types/ICharacteristicsSearch";
import { DictionaryDto, TableColumn } from "../../../../lib/interfaces";
import { ReactComponent as PlusIcon } from "../../../../img/plus.svg";
import { ReactComponent as DeleteIcon } from "../../../../img/delete.svg";
import { ReactComponent as ThreeDots } from "../../../../img/3dotsButton.svg";
import "./CharacteristicsTable.less";
import RequiredMark from "../../../../components/common/RequiredMark/RequiredMark";
import { DataTypeIdValue } from "./CharacteristicsTableConst/CharacteristicsTableConst";
import { checkIsLoading, displayFormat, toDisplayFormat } from "../../../../lib/utils";
import ModalWindow from "../../../../components/ModalWindow/ModalWindow";
import { Characteristic } from "../../../../lib/types/Offer";
import { FieldsError } from "../../pages/CharacteristicsContainer/utils/useCharacteristicsInitialisationType";
import { ReactComponent as WarningIcon } from "../../../../img/warning-round-icon.svg";
import { getRequiredCharacteristics } from "./CharacteristicTableUtils/getRequiredCharacteristic";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";

const columnsWidths = {
  objectId: "200px",
  objectCharTypeId: "25%",
  specificDicId: "20%",
  values: "20%",
  defaultValue: "20%",
};

interface IProps {
  mode?: "create" | "view" | "edit" | "add" | "close";
  isInheritableObject?: boolean;
  objectTypeId?: number;
  isSelectedObjectId?: boolean;
  tableData: TableDataCharacteristic[];
  setTableData: React.Dispatch<React.SetStateAction<TableDataCharacteristic[]>>;
  dataSaved?: Characteristic[];
  valuesTitle?: string;
  setIsCollapseOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newObjectChars?: Characteristic[];
  isNewObjectChars?: boolean;
  isDefaultDisabled?: boolean;
  disabledValidation?: boolean;
  fieldsError?: FieldsError;
  isChildCharacteristic?: boolean;
  fromDateCharacteristic?: string | null;
  isUpdateParams?: { isDisabled: boolean; isClearTable: boolean };
}

export const CharacteristicsTable = ({
  mode,
  isInheritableObject,
  isSelectedObjectId,
  objectTypeId,
  tableData,
  setTableData,
  dataSaved,
  valuesTitle,
  setIsCollapseOpen,
  newObjectChars,
  isNewObjectChars,
  isDefaultDisabled,
  disabledValidation,
  fieldsError,
  isChildCharacteristic,
  fromDateCharacteristic,
  isUpdateParams,
}: IProps) => {
  const {
    objectCharTypes,
    characteristicDictionaries,
    objectTypes,
    objectTypesLoading,
    objectCharTypesLoading,
    characteristicDictionariesLoading,
    characteristicsUnitsDic,
  } = useAppSelector((state) => state.characteristics);

  // Характеристики именно для конкретного objectTypeId, если есть
  const [findObjectCharTypes, setFindObjectCharTypes] = useState<IObjectCharTypes<string>[]>([]);

  // Выбранная характеристика для модалки значений (если есть объект, значит показываем модалку)
  const [visibleModalCharacteristic, setVisibleModalCharacteristic] = useState<
    Partial<IObjectCharTypes<string> & { key?: string }>
  >();

  const [visibleModalCharacteristicView, setVisibleModalCharacteristicView] = useState<
    Partial<IObjectCharTypes<string> & { key?: string; specificDicId?: number }>
  >();

  // Выбранные id значений в модалке просмотра значений для (dataTypeId === 4)
  const [selectedCharacteristicValues, setSelectedCharacteristicValues] = useState<number[]>([]);

  // Выбранные id значений в модалке просмотра значений для (dataTypeId !== 4)
  const [selectedCharacteristicValueDefault, setSelectedCharacteristicValueDefault] = useState<
    number[]
  >([]);

  // Выбранные значения в модалке просмотра значений для (dataTypeId !== 4)
  const [characteristicValues, setCharacteristicValues] = useState<CharacteristicValues[]>([]);

  // Отбираем характеристики которые относятся к objectTypeId у не наследуемых или наследуемых без выбора к изменению объекта
  useEffect(() => {
    if (objectCharTypes?.length > 0) {
      const findObjectCharTypesArray = objectCharTypes?.flatMap((el) =>
        el.objectTypeId === objectTypeId ? el : []
      );
      setFindObjectCharTypes(findObjectCharTypesArray);
    }
  }, [objectCharTypes]);

  // Получаем id значения характеристики (так как под капотом id у defaultValue отсутствует (приходиться искать по value))
  const getId = ({
    paramVal,
    value,
    objectCharTypeId,
  }: {
    paramVal: CharacteristicValues;
    value: string | number | moment.Moment | null | undefined;
    objectCharTypeId?: number;
  }) => {
    const objectCharTypeValues = objectCharTypes?.find((el) => el.id === objectCharTypeId)
      ?.objectCharTypeValues;

    return objectCharTypeValues?.find((el) => {
      if (paramVal?.valDate) {
        return moment(el?.charTypeValue.value).isSame(moment(paramVal.valDate));
      }

      return String(el?.charTypeValue.value) === String(value);
    })?.id;
  };

  // Получаем значение характеристики в зависимости от параметра
  const getValue = ({
    value,
    objectCharTypeValues,
  }: {
    value: CharacteristicValues;
    objectCharTypeValues?: IObjectCharTypeValue<string>[];
  }) => {
    if (value?.valDate) {
      return objectCharTypeValues && objectCharTypeValues.length > 0
        ? value.valDate
        : moment(value.valDate);
    }
    if (value?.valDicId) return value.valDicId;
    if (value?.valStr) return value.valStr;
    if (value?.valNum !== null) return value.valNum;
    return null;
  };

  // Получаем дату характеристики в зависимости от параметра
  const getValueDate = ({
    value,
    objectCharTypeValues,
  }: {
    value: CharacteristicValues;
    objectCharTypeValues?: IObjectCharTypeValue<string>[];
  }) => {
    if (value?.valDate) {
      if (objectCharTypeValues && objectCharTypeValues.length > 0) {
        return value.valDate || undefined;
      }
      return moment(value.valDate) || undefined;
    }

    return undefined;
  };

  // Получаем данные характеристики
  const getCharData = ({
    characteristic,
  }: {
    characteristic: Characteristic;
  }): [IObjectCharTypes<string>, IObjectCharTypeValue<string>[] | undefined] => {
    const characteristicObjectCharType = (characteristic.objectCharType as unknown) as IObjectCharTypes<string>;

    const objectCharTypeValues = characteristicObjectCharType?.objectCharTypeValues?.length
      ? characteristicObjectCharType.objectCharTypeValues
      : objectCharTypes.find((el) => el.id === characteristic.objectCharType?.id)
          ?.objectCharTypeValues;

    return [characteristicObjectCharType, objectCharTypeValues];
  };

  // Формирование данных таблицы
  useEffect(() => {
    const requiredCharacteristics = getRequiredCharacteristics({
      isInheritableObject,
      isSelectedObjectId,
      objectTypeId,
    });

    // Функция проверки является ли строка обязательной
    const getIsRequired = (index: number, characteristic: Characteristic) => {
      return (
        index < requiredCharacteristics.length &&
        requiredCharacteristics.some(
          (el) => el.characteristic.id === characteristic.objectCharTypeId
        )
      );
    };

    // Функция формирования данных таблицы
    const getFormatTableData = ({
      newTableData,
      // Если true, то фильтруем характеристики которые уже есть в таблице
      isFiltered = false,
      // Если true, то добавляем обязательные характеристики
      isAddRequired = false,
    }: {
      newTableData?: TableDataCharacteristic[];
      isFiltered?: boolean;
      isAddRequired?: boolean;
    }) => {
      const filteredRequiredCharacteristics = isFiltered
        ? requiredCharacteristics.filter(
            (el) =>
              !newTableData?.find((tableEl) => el.characteristic.id === tableEl?.characteristic?.id)
          )
        : requiredCharacteristics;

      const tableDataFormat: TableDataCharacteristic[] = [
        ...(isAddRequired ? filteredRequiredCharacteristics : []),
        ...(newTableData || []),
      ];

      setTableData(tableDataFormat);
    };

    if (mode === "add" || mode === "create") {
      // Если пользователь выбрал не перезапрашивать характеристики
      if (isUpdateParams?.isDisabled) {
        return;
      }

      // Если пользователь выбрал перезапрашивать характеристики
      if (!isUpdateParams?.isDisabled && isUpdateParams?.isClearTable) {
        getFormatTableData({ isAddRequired: true });
        return;
      }
    }

    if (disabledValidation) {
      if (!isNewObjectChars) {
        if (mode === "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,

                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            getFormatTableData({ newTableData });
          }
        }
        if (mode !== "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,
                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            getFormatTableData({ newTableData });
          } else {
            getFormatTableData({});
          }
        }
      }
      if (isNewObjectChars) {
        if (mode === "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,

                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            getFormatTableData({ newTableData });
          }
        }
        if (mode !== "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              const data = {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,

                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id =
                    getId({
                      paramVal: el,
                      value,
                      objectCharTypeId: characteristic.objectCharType?.id,
                    }) || Number(uniqueId());

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };

              // Подставляем в возможные значения только то что приходит в newObjectChars
              if (newObjectChars && newObjectChars?.length > 0) {
                if (
                  data.characteristic.charType.dataTypeId !== DataTypeIdValue["Словарный термин"]
                ) {
                  const findChar = newObjectChars?.find(
                    (el) => el?.objectCharTypeId === characteristic?.objectCharType?.id
                  );

                  data.characteristic.objectCharTypeValues = findChar?.values?.map((valEl) => {
                    const findValue =
                      data?.values && data?.values?.length > 0
                        ? data?.values?.find((charVal) => {
                            const value = getValue({ value: charVal, objectCharTypeValues });
                            const valueObjectChar = getValue({
                              value: valEl,
                              objectCharTypeValues,
                            });

                            if (
                              data.characteristic.charType.dataTypeId === DataTypeIdValue["Дата"]
                            ) {
                              return moment(value).isSame(moment(valueObjectChar))
                                ? charVal
                                : undefined;
                            }
                            return value === valueObjectChar ? charVal : undefined;
                          })
                        : undefined;

                    return {
                      charTypeValue: {
                        id: findValue?.id,
                        charTypeId: 0,
                        valueTypeId: 0,
                        unitTypeId: 0,
                        value: valEl.valDate || valEl.valDicId || valEl.valStr || valEl.valNum,
                        valueTo: "",
                        rangeTypeId: 0,
                      },
                      id: findValue?.id,
                      objectCharTypeId: 0,
                      charTypeValueId: 0,
                    };
                  });
                } else {
                  const findChar = newObjectChars?.find(
                    (el) => el?.specificDicId === characteristic.specificDicId
                  );

                  data.characteristic = {
                    ...data.characteristic,
                    objectCharTypeValues: findChar?.values?.map((valEl) => ({
                      charTypeValue: {
                        id: Number(uniqueId()),
                        charTypeId: 0,
                        valueTypeId: 0,
                        unitTypeId: 0,
                        value: valEl.valDate || valEl.valDicId || valEl.valStr || valEl.valNum,
                        valueTo: "",
                        rangeTypeId: 0,
                      },
                      id: Number(uniqueId()),
                      objectCharTypeId: 0,
                      charTypeValueId: 0,
                    })),
                  };
                }
              }

              return data;
            });

            getFormatTableData({ newTableData });
          } else {
            getFormatTableData({});
          }
        }
      }
    } else {
      if (!isNewObjectChars) {
        if (mode === "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,
                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            getFormatTableData({ newTableData, isFiltered: true, isAddRequired: true });
          } else if (findObjectCharTypes?.length > 0) {
            getFormatTableData({ isAddRequired: true });
          }
        }

        if (mode !== "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,
                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            if (mode !== "view") {
              getFormatTableData({ newTableData, isFiltered: true, isAddRequired: true });
              return;
            }

            getFormatTableData({ newTableData });
          } else {
            if (mode !== "view") {
              getFormatTableData({ isAddRequired: true });
              return;
            }
            getFormatTableData({});
          }
        }
      }
      if (isNewObjectChars) {
        if (mode !== "create") {
          if (dataSaved && dataSaved?.length > 0) {
            const newTableData = dataSaved.flatMap((characteristic, index) => {
              const [characteristicObjectCharType, objectCharTypeValues] = getCharData({
                characteristic,
              });

              // Количество обязательных всегда одинаково
              const isRequired = getIsRequired(index, characteristic);

              const characteristicFormat = {
                ...characteristicObjectCharType,
                objectCharTypeValues,
              };

              return {
                key: `id=${characteristic.objectCharType?.id}_unicKey=${uniqueId()}`,
                isRequired,
                objectCharTypeId: characteristic?.objectCharType?.id,
                specificDicId: characteristic?.specificDicId,
                objectId: characteristic.objectTypeId,
                characteristic: characteristicFormat,

                values: characteristic?.values?.map((el) => {
                  const value = getValue({ value: el, objectCharTypeValues });
                  const valDate = getValueDate({ value: el, objectCharTypeValues });
                  const id = getId({
                    paramVal: el,
                    value,
                    objectCharTypeId: characteristic.objectCharType?.id,
                  });

                  return {
                    ...el,
                    id,
                    valDate,
                    value,
                  };
                }),
              };
            });

            getFormatTableData({ newTableData });
          } else {
            getFormatTableData({});
          }
        }
      }
    }
  }, [
    findObjectCharTypes,
    dataSaved,
    newObjectChars,
    isNewObjectChars,
    mode,
    objectCharTypes,
    isUpdateParams,
  ]);

  // Раскрытие раздела если есть обязательные или при наличии данных в создании на основании
  useEffect(() => {
    if (fromDateCharacteristic) {
      if (dataSaved?.length && mode === "create") {
        setIsCollapseOpen(true);
      }

      if (
        getRequiredCharacteristics({
          isInheritableObject,
          isSelectedObjectId,
          objectTypeId,
        })?.length &&
        !disabledValidation &&
        !isNewObjectChars
      ) {
        if (mode === "create" || mode === "add" || mode === "edit") {
          setIsCollapseOpen(true);
        }
      }
    }
  }, [findObjectCharTypes, disabledValidation, mode, dataSaved, fromDateCharacteristic]);

  // Функция для генерации полей для разного типа ввода
  const getFieldForTypeCharacteristic = (
    characteristic?: Partial<IObjectCharTypes<string>>,
    value?: string | number | null | moment.Moment,
    onChange?: ({
      newValue,
      searchId,
    }: {
      newValue?: string | number | null | moment.Moment;
      searchId?: number;
    }) => void,
    searchId?: number,
    isError?: boolean
  ): JSX.Element | null => {
    const classNameError = isError ? "characteristics-table__selectError" : "";

    if (characteristic?.charType?.dataTypeId === DataTypeIdValue.Число) {
      if (
        !characteristic?.objectCharTypeValues ||
        characteristic?.objectCharTypeValues?.length === 0
      ) {
        return (
          <InputNumber
            placeholder="введите число"
            style={{ width: "100%" }}
            value={value !== undefined && value !== null ? Number(value) : undefined}
            onChange={(e) => onChange && onChange({ newValue: e, searchId })}
            className={classNameError}
          />
        );
      }

      // Минимальное значение
      let rangeFrom = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.value || 0);
      // Максимальное значение
      let rangeTo = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.valueTo || 0);

      const rangeTypeId = characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.rangeTypeId;

      // В зависимости от rangeTypeId, включаем вхождение в диапазон или нет
      switch (rangeTypeId) {
        // Граница интервала включены в диапазон значений
        case 1:
          // Ничего не меняем, так как так и заполнено по умолчанию
          break;
        // Границы интервала не включены в диапазон значений
        case 2:
          rangeFrom += 1;
          rangeTo -= 1;
          break;
        // Начало диапазона не включено в интервал
        case 3:
          rangeFrom += 1;
          break;
        // Окончание диапазона не включено в интервал
        case 4:
          rangeTo -= 1;
          break;
      }

      return (
        <InputNumber
          placeholder={`введите число от ${rangeFrom} до ${rangeTo}`}
          style={{ width: "100%" }}
          min={rangeFrom}
          max={rangeTo}
          value={value !== undefined && value !== null ? Number(value) : undefined}
          onChange={(e) => onChange && onChange({ newValue: e, searchId })}
          className={classNameError}
        />
      );
    } // Если есть список своё не даём написать
    if (characteristic?.charType?.dataTypeId === DataTypeIdValue.Строка) {
      if (
        !characteristic?.objectCharTypeValues ||
        characteristic?.objectCharTypeValues?.length === 0
      ) {
        return (
          <Input
            placeholder="введите строку"
            style={{ width: "100%" }}
            value={String(value || "")}
            onChange={(e) => onChange && onChange({ newValue: e.target.value, searchId })}
            className={classNameError}
          />
        );
      }

      return (
        <Input
          placeholder="введите строку"
          style={{ width: "100%" }}
          value={String(value || "")}
          onChange={(e) => onChange && onChange({ newValue: e.target.value, searchId })}
          className={classNameError}
        />
      );
    } // Если есть список своё не даём написать
    if (characteristic?.charType?.dataTypeId === DataTypeIdValue.Дата) {
      if (
        !characteristic?.objectCharTypeValues ||
        characteristic?.objectCharTypeValues?.length === 0
      ) {
        return (
          <DatePicker
            placeholder="выберите дату"
            style={{ width: "100%" }}
            value={value ? moment(value) : undefined}
            onChange={(e) => onChange && onChange({ newValue: e, searchId })}
            format={displayFormat}
            className={classNameError}
          />
        );
      }

      return (
        <DatePicker
          placeholder="выберите дату"
          style={{ width: "100%" }}
          value={value ? moment(value) : undefined}
          onChange={(e) => onChange && onChange({ newValue: e, searchId })}
          className={classNameError}
        />
      );
    } // Если есть список своё не даём написать
    if (characteristic?.charType?.dataTypeId === DataTypeIdValue["Словарный термин"]) {
      // Ищем справочник который соответствует (id = valueDicTypeAlias)
      const finDic = characteristicDictionaries?.find(
        (dic) =>
          dic?.dicType === characteristic?.charType?.valueDicTypeAlias ||
          dic?.data?.[0]?.dicTypeId === characteristic?.charType?.valueDicTypeId
      )?.data;

      return (
        <Select
          placeholder="выберите значения"
          style={{ width: "100%" }}
          value={value ? Number(value) : undefined}
          onChange={(e) => onChange && onChange({ newValue: e, searchId })}
          className={classNameError}
        >
          {finDic &&
            finDic.map((el) => {
              return <Select.Option value={el.id}>{el.name}</Select.Option>;
            })}
        </Select>
      );
    }
    if (characteristic?.charType?.dataTypeId === DataTypeIdValue["Целое число"]) {
      if (
        !characteristic?.objectCharTypeValues ||
        characteristic?.objectCharTypeValues?.length === 0
      ) {
        return (
          <InputNumber
            placeholder="введите целое число"
            style={{ width: "100%" }}
            precision={0}
            value={value !== undefined && value !== null ? Number(value) : undefined}
            onChange={(e) => onChange && onChange({ newValue: e, searchId })}
            className={classNameError}
          />
        );
      }

      // Минимальное значение
      let rangeFrom = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.value || 0);
      // Максимальное значение
      let rangeTo = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.valueTo || 0);

      const rangeTypeId = characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.rangeTypeId;

      // В зависимости от rangeTypeId, включаем вхождение в диапазон или нет
      switch (rangeTypeId) {
        // Граница интервала включены в диапазон значений
        case 1:
          // Ничего не меняем, так как так и заполнено по умолчанию
          break;
        // Границы интервала не включены в диапазон значений
        case 2:
          rangeFrom += 1;
          rangeTo -= 1;
          break;
        // Начало диапазона не включено в интервал
        case 3:
          rangeFrom += 1;
          break;
        // Окончание диапазона не включено в интервал
        case 4:
          rangeTo -= 1;
          break;
      }

      return (
        <InputNumber
          placeholder={`введите целое число от ${rangeFrom} до ${rangeTo}`}
          style={{ width: "100%" }}
          min={rangeFrom}
          max={rangeTo}
          precision={0}
          value={value !== undefined && value !== null ? Number(value) : undefined}
          onChange={(e) => onChange && onChange({ newValue: e, searchId })}
          className={classNameError}
        />
      );
    } // Если есть список своё не даём написать
    if (characteristic?.charType?.dataTypeId === DataTypeIdValue["Натуральное число"]) {
      if (
        !characteristic?.objectCharTypeValues ||
        characteristic?.objectCharTypeValues?.length === 0
      ) {
        return (
          <InputNumber
            placeholder="введите натуральное число"
            style={{ width: "100%" }}
            value={value !== undefined && value !== null ? Number(value) : undefined}
            onChange={(e) => onChange && onChange({ newValue: e, searchId })}
            className={classNameError}
          />
        );
      }

      // Минимальное значение
      let rangeFrom = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.value || 0);
      // Максимальное значение
      let rangeTo = Number(characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.valueTo || 0);

      const rangeTypeId = characteristic?.objectCharTypeValues?.[0]?.charTypeValue?.rangeTypeId;

      // В зависимости от rangeTypeId, включаем вхождение в диапазон или нет
      switch (rangeTypeId) {
        // Граница интервала включены в диапазон значений
        case 1:
          // Ничего не меняем, так как так и заполнено по умолчанию
          break;
        // Границы интервала не включены в диапазон значений
        case 2:
          rangeFrom += 1;
          rangeTo -= 1;
          break;
        // Начало диапазона не включено в интервал
        case 3:
          rangeFrom += 1;
          break;
        // Окончание диапазона не включено в интервал
        case 4:
          rangeTo -= 1;
          break;
      }

      return (
        <InputNumber
          placeholder={`введите натуральное число от ${rangeFrom} до ${rangeTo}`}
          style={{ width: "100%" }}
          min={rangeFrom}
          max={rangeTo}
          value={value !== undefined && value !== null ? Number(value) : undefined}
          onChange={(e) => onChange && onChange({ newValue: e, searchId })}
          className={classNameError}
        />
      );
    } // Если есть список своё не даём написать
    return null;
  };

  // Формируем список характеристик доступных к выбору
  const getOptionsCharacteristics = (record: TableDataCharacteristic) => {
    let options: IObjectCharTypes<string>[] | Characteristic[] | undefined = [];

    // Для жёстко заданных характеристик
    if (isNewObjectChars) {
      // Уникальные характеристики
      const filteredNewObjectChars: Characteristic[] = [];

      // Оставляем только уникальные характеристики
      const _ = newObjectChars?.forEach((el) => {
        const findDuplicateIndex = filteredNewObjectChars.findIndex(
          (filterItem) => filterItem.objectCharTypeId === el.objectCharTypeId
        );

        if (findDuplicateIndex === -1) {
          filteredNewObjectChars.push(el);
        } else {
          filteredNewObjectChars.splice(findDuplicateIndex, 1, {
            ...filteredNewObjectChars[findDuplicateIndex],
            values: [
              ...(filteredNewObjectChars[findDuplicateIndex].values || []),
              ...(el?.values || []),
            ],
          });
        }
      });

      options = filteredNewObjectChars?.filter((filterEl) => {
        // Для значений которые имеют specificDicTypeId
        if (filterEl.objectCharType?.charType.specificDicTypeId) {
          // Поиск характеристик которые совпадают с objectCharTypeId элемента
          const findAllObjectCharTypeId = tableData.filter(
            (tableEl) =>
              filterEl.objectCharType?.id &&
              tableEl.objectCharTypeId &&
              filterEl.objectCharType?.id === tableEl.objectCharTypeId &&
              tableEl.key !== record.key
          );

          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === filterEl?.objectCharType?.charType?.specificDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === filterEl?.objectCharType?.charType?.specificDicTypeId
          )?.data;

          const filteredSpecificDicId: number[] = [];

          // Оставляем только уникальные характеристики
          const _ = newObjectChars?.forEach((el) => {
            if (el?.objectCharTypeId === filterEl?.objectCharTypeId) {
              filteredSpecificDicId.push(Number(el.specificDicId));
            }
          });

          if (
            finDic &&
            findAllObjectCharTypeId.length > 0 &&
            findAllObjectCharTypeId?.length >= Number(filteredSpecificDicId?.length)
          ) {
            return false;
          }
        }

        // Для значений которые не имеют specificDicTypeId
        if (!filterEl?.objectCharType?.charType.specificDicTypeId) {
          // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
          const findObjectCharTypeId = tableData.find(
            (tableEl) =>
              filterEl.objectCharType?.id &&
              tableEl.objectCharTypeId &&
              filterEl.objectCharType?.id === tableEl.objectCharTypeId &&
              tableEl.key !== record.key
          );

          // Убираем этот элемент
          if (findObjectCharTypeId) {
            return false;
          }
        }

        return true;
      });
    }

    // Для обычных характеристик из поиска
    if (!isNewObjectChars) {
      if (isSelectedObjectId) {
        options = objectCharTypes
          ?.flatMap((el) => (el.objectTypeId === record.objectId ? el : []))
          .filter((el) => {
            // Для значений которые имеют specificDicTypeId
            if (el.charType?.specificDicTypeId) {
              // Поиск характеристик которые совпадают с objectCharTypeId элемента
              const findAllObjectCharTypeId = tableData.filter(
                (tableEl) =>
                  el.id &&
                  tableEl.objectCharTypeId &&
                  el.id === tableEl.objectCharTypeId &&
                  tableEl.key !== record.key
              );

              const finDic = characteristicDictionaries?.find(
                (dic) =>
                  dic?.dicType === el?.charType?.specificDicTypeAlias ||
                  dic?.data?.[0]?.dicTypeId === el?.charType?.specificDicTypeId
              )?.data;

              if (
                finDic &&
                findAllObjectCharTypeId.length > 0 &&
                findAllObjectCharTypeId?.length >= finDic.length
              ) {
                return false;
              }
            }

            // Для значений которые не имеют specificDicTypeId
            if (!el.charType?.specificDicTypeId) {
              // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
              const findObjectCharTypeId = tableData.find(
                (tableEl) =>
                  el.id &&
                  tableEl.objectCharTypeId &&
                  el.id === tableEl.objectCharTypeId &&
                  tableEl.key !== record.key
              );

              // Убираем этот элемент
              if (findObjectCharTypeId) {
                return false;
              }
            }

            return true;
          });
      }

      if (!isSelectedObjectId) {
        options = findObjectCharTypes
          // ?.filter((el) => {
          //   // Cписок характеристик, доступных для объекта, за исключением обязательных без словаря-параметра.
          //   if (el?.minCardinality >= 1 && !el.charType?.specificDicTypeId) {
          //     return false;
          //   }
          //   return true;
          // })
          .filter((el) => {
            // Для значений которые имеют specificDicTypeId
            if (el.charType?.specificDicTypeId) {
              // Поиск характеристик которые совпадают с objectCharTypeId элемента
              const findAllObjectCharTypeId = tableData.filter(
                (tableEl) =>
                  el.id &&
                  tableEl.objectCharTypeId &&
                  el.id === tableEl.objectCharTypeId &&
                  tableEl.key !== record.key
              );

              const finDic = characteristicDictionaries?.find(
                (dic) =>
                  dic?.dicType === el?.charType?.specificDicTypeAlias ||
                  dic?.data?.[0]?.dicTypeId === el?.charType?.specificDicTypeId
              )?.data;

              if (
                finDic &&
                findAllObjectCharTypeId.length > 0 &&
                findAllObjectCharTypeId?.length >= finDic.length
              ) {
                return false;
              }
            }

            // Для значений которые не имеют specificDicTypeId
            if (!el.charType?.specificDicTypeId) {
              // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
              const findObjectCharTypeId = tableData.find(
                (tableEl) =>
                  el.id &&
                  tableEl.objectCharTypeId &&
                  el.id === tableEl.objectCharTypeId &&
                  tableEl.key !== record.key
              );

              // Убираем этот элемент
              if (findObjectCharTypeId) {
                return false;
              }
            }

            return true;
          });
      }
    }

    if (record.objectCharTypeId && options?.length === 0 && record.characteristic) {
      options.push(({
        objectCharType: record.characteristic,
        objectCharTypeId: record.objectCharTypeId,
        objectTypeId: Number(record.objectId),
        values: Array.isArray(record.values) && record.values?.length > 0 ? record.values : [],
      } as unknown) as IObjectCharTypes<string>);
    }

    return options;
  };

  // Узнаём длину характеристик доступных к выбору
  const getOptionsCharacteristicsLength = () => {
    let options: IObjectCharTypes<string>[] | Characteristic[] | undefined = [];

    // Для жёстко заданных характеристик
    if (isNewObjectChars) {
      // Уникальные характеристики
      const filteredNewObjectChars: Characteristic[] = [];

      // Оставляем только уникальные характеристики
      const _ = newObjectChars?.forEach((el) => {
        const findDuplicateIndex = filteredNewObjectChars.findIndex(
          (filterItem) => filterItem.objectCharTypeId === el.objectCharTypeId
        );

        if (findDuplicateIndex === -1) {
          filteredNewObjectChars.push(el);
        } else {
          filteredNewObjectChars.splice(findDuplicateIndex, 1, {
            ...filteredNewObjectChars[findDuplicateIndex],
            values: [
              ...(filteredNewObjectChars[findDuplicateIndex].values || []),
              ...(el?.values || []),
            ],
          });
        }
      });
      options = filteredNewObjectChars?.filter((filterEl) => {
        // Для значений которые имеют specificDicTypeId
        if (filterEl.objectCharType?.charType.specificDicTypeId) {
          // Поиск характеристик которые совпадают с objectCharTypeId элемента
          const findAllObjectCharTypeId = tableData.filter(
            (tableEl) =>
              filterEl.objectCharType?.id &&
              tableEl.objectCharTypeId &&
              filterEl.objectCharType?.id === tableEl.objectCharTypeId
          );

          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === filterEl?.objectCharType?.charType?.specificDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === filterEl?.objectCharType?.charType?.specificDicTypeId
          )?.data;

          const filteredSpecificDicId: number[] = [];

          // Оставляем только уникальные характеристики
          const _ = newObjectChars?.forEach((el) => {
            if (el?.objectCharTypeId === filterEl?.objectCharTypeId) {
              filteredSpecificDicId.push(Number(el.specificDicId));
            }
          });

          if (
            finDic &&
            findAllObjectCharTypeId.length > 0 &&
            findAllObjectCharTypeId?.length >= Number(filteredSpecificDicId?.length)
          ) {
            return false;
          }
        }

        // Для значений которые не имеют specificDicTypeId
        if (!filterEl?.objectCharType?.charType.specificDicTypeId) {
          // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
          const findObjectCharTypeId = tableData.find(
            (tableEl) =>
              filterEl.objectCharType?.id &&
              tableEl.objectCharTypeId &&
              filterEl.objectCharType?.id === tableEl.objectCharTypeId
          );

          // Убираем этот элемент
          if (findObjectCharTypeId) {
            return false;
          }
        }

        return true;
      });
    }

    // Для обычных характеристик из поиска
    if (!isNewObjectChars) {
      if (isSelectedObjectId) {
        options = objectCharTypes.filter((el) => {
          // Для значений которые имеют specificDicTypeId
          if (el.charType?.specificDicTypeId) {
            // Поиск характеристик которые совпадают с objectCharTypeId элемента
            const findAllObjectCharTypeId = tableData.filter(
              (tableEl) => el.id && tableEl.objectCharTypeId && el.id === tableEl.objectCharTypeId
            );

            const finDic = characteristicDictionaries?.find(
              (dic) =>
                dic?.dicType === el?.charType?.specificDicTypeAlias ||
                dic?.data?.[0]?.dicTypeId === el?.charType?.specificDicTypeId
            )?.data;

            if (
              finDic &&
              findAllObjectCharTypeId.length > 0 &&
              findAllObjectCharTypeId?.length >= finDic.length
            ) {
              return false;
            }
          }

          // Для значений которые не имеют specificDicTypeId
          if (!el.charType?.specificDicTypeId) {
            // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
            const findObjectCharTypeId = tableData.find(
              (tableEl) => el.id && tableEl.objectCharTypeId && el.id === tableEl.objectCharTypeId
            );

            // Убираем этот элемент
            if (findObjectCharTypeId) {
              return false;
            }
          }

          return true;
        });
      }

      if (!isSelectedObjectId) {
        options = findObjectCharTypes.filter((el) => {
          // Для значений которые имеют specificDicTypeId
          if (el.charType?.specificDicTypeId) {
            // Поиск характеристик которые совпадают с objectCharTypeId элемента
            const findAllObjectCharTypeId = tableData.filter(
              (tableEl) => el.id && tableEl.objectCharTypeId && el.id === tableEl.objectCharTypeId
            );

            const finDic = characteristicDictionaries?.find(
              (dic) =>
                dic?.dicType === el?.charType?.specificDicTypeAlias ||
                dic?.data?.[0]?.dicTypeId === el?.charType?.specificDicTypeId
            )?.data;

            if (
              finDic &&
              findAllObjectCharTypeId.length > 0 &&
              findAllObjectCharTypeId?.length >= finDic.length
            ) {
              return false;
            }
          }

          // Для значений которые не имеют specificDicTypeId
          if (!el.charType?.specificDicTypeId) {
            // Поиск характеристики которая совпадает с objectCharTypeId элемента в tableData и не является этой строкой
            const findObjectCharTypeId = tableData.find(
              (tableEl) => el.id && tableEl.objectCharTypeId && el.id === tableEl.objectCharTypeId
            );

            // Убираем этот элемент
            if (findObjectCharTypeId) {
              return false;
            }
          }

          return true;
        });
      }
    }

    return options?.length;
  };

  const getColumns = (): TableColumn<TableDataCharacteristic>[] => {
    return [
      {
        key: "objectId",
        dataIndex: "objectId",
        title: "Объект",
        hidden: !isInheritableObject,
        width: columnsWidths.objectId,
        render: (value, record) => {
          // Если это наследуемое и есть жёсткий объект по которому отображать характеристики
          if (isInheritableObject && objectTypeId) {
            return objectTypes?.find((el) => el.id === value)?.name;
          }

          if (mode === "create" || mode === "add" || mode === "edit") {
            const onSelectObjectCharacteristic = (selectId: number): void => {
              setTableData((prev) => {
                const newTableData = prev.map((el) => {
                  if (record.key === el.key) {
                    return {
                      ...el,
                      objectId: selectId,

                      objectCharTypeId: undefined,
                      specificDicId: undefined,
                    };
                  }
                  return el;
                });
                return newTableData;
              });
            };

            return (
              <Select
                value={value}
                style={{ width: "180px" }}
                onChange={onSelectObjectCharacteristic}
                showSearch
                filterOption={(input, option) => {
                  if (!option || !option.name) return false;
                  const lowerCaseInput = input.toLowerCase();
                  const lowerCaseName = option.name.toLowerCase();
                  return lowerCaseName.includes(lowerCaseInput);
                }}
                disabled={
                  (mode === "add" || mode === "edit") &&
                  record.characteristic?.accessModeId !== 2 &&
                  !record.isNew &&
                  !disabledValidation
                }
              >
                {objectTypes?.length > 0
                  ? [...objectTypes]
                      .sort((a, b) => {
                        // Проверяем, является ли элемент числом
                        const isANumber = !isNaN(Number(a?.name));
                        const isBNumber = !isNaN(Number(b?.name));

                        // Если оба элемента — числа, то сохраняем порядок
                        if (isANumber && isBNumber) {
                          return 0;
                        }

                        // Если первый элемент — число, а второй — нет, отправляем его в конец
                        if (isANumber) {
                          return 1;
                        }

                        // Если второй элемент — число, а первый — нет, отправляем его в конец
                        if (isBNumber) {
                          return -1;
                        }

                        // Если оба элемента строки, сортируем по алфавиту
                        return a?.name?.localeCompare(b?.name);
                      })
                      .map((objType) => {
                        return (
                          <Select.Option key={objType.id} value={objType.id} name={objType.name}>
                            {objType.name}
                          </Select.Option>
                        );
                      })
                  : null}
              </Select>
            );
          }

          if (mode === "view") {
            return objectTypes?.find((el) => el.id === value)?.name;
          }
        },
      },
      {
        key: "objectCharTypeId",
        dataIndex: "objectCharTypeId",
        title: "",
        hidden: false,
        width: "2px",
        render(_, record) {
          // Проверка на то, что характеристика не существует на дату создания
          const isDateCharacteristicOutOfRange = !moment(fromDateCharacteristic).isBetween(
            record.characteristic?.fromDate,
            record.characteristic?.toDate,
            "day",
            "[]"
          );

          // Проверка на то, что значение характеристики не существует на дату создания
          const isDateValueOutOfRange = record.values?.find(({ id }) => {
            const findObjectCharTypeValue = record.characteristic?.objectCharTypeValues?.find(
              (el) => el.id === id
            );

            if (findObjectCharTypeValue) {
              return !moment(fromDateCharacteristic).isBetween(
                findObjectCharTypeValue.fromDate,
                findObjectCharTypeValue.toDate,
                "day",
                "[]"
              );
            }

            return false;
          });

          return (
            <Row align="middle" wrap={false} style={{ position: "absolute" }}>
              {mode === "add" &&
                record.objectCharTypeId &&
                (isDateCharacteristicOutOfRange || isDateValueOutOfRange) && (
                  <Tooltip
                    title="Характеристика или значение отсутствует на дату создания"
                    mouseLeaveDelay={0}
                  >
                    <WarningIcon
                      // Если это наследуемое и есть жёсткий объект по которому отображать характеристики
                      // то отступаем вправо на 26px иначе на 10px
                      style={{ margin: `-12px 0px 0px -${!isInheritableObject ? 10 : 27}px` }}
                    />
                  </Tooltip>
                )}
            </Row>
          );
        },
      },
      {
        key: "objectCharTypeId",
        dataIndex: "objectCharTypeId",
        title: "Характеристика",
        hidden: false,
        width: columnsWidths.objectCharTypeId,
        render(value, record) {
          if (mode === "create" || mode === "add" || mode === "edit") {
            const getCharacteristicOptions = (record: TableDataCharacteristic) => {
              return getOptionsCharacteristics(record)?.sort((a, b) => {
                const isANumber = !Number.isNaN(Number(a?.name));
                const isBNumber = !Number.isNaN(Number(b?.name));

                if (isANumber && isBNumber) return 0;
                if (isANumber) return 1;
                if (isBNumber) return -1;

                return String(a?.name)?.localeCompare(String(b?.name));
              });
            };

            const renderSelectOptions = (isNewObjectChars: boolean) => {
              return (characteristic: any) => (
                <Select.Option
                  key={isNewObjectChars ? characteristic?.objectCharType?.id : characteristic.id}
                  value={isNewObjectChars ? characteristic?.objectCharType?.id : characteristic.id}
                >
                  {isNewObjectChars ? characteristic?.objectCharType?.name : characteristic?.name}
                </Select.Option>
              );
            };

            const filterOption = (input: string, option: any) => {
              if (!option || !option?.children) return false;
              return String(option.children).toLowerCase().includes(input.toLowerCase());
            };

            const renderSelect = (
              record: TableDataCharacteristic,
              onSelectCharacteristic: (selectId: number) => void,
              disabled: boolean
            ) => {
              return (
                <Select
                  style={{ width: "85%" }}
                  value={record.objectCharTypeId}
                  onChange={onSelectCharacteristic}
                  disabled={disabled}
                  showSearch
                  filterOption={filterOption}
                >
                  {getCharacteristicOptions(record)?.map(renderSelectOptions(!!isNewObjectChars))}
                </Select>
              );
            };

            if (disabledValidation) {
              const onSelectCharacteristic = (selectId: number) => {
                const findCharacteristic = objectCharTypes.find((el) => el.id === selectId);

                setTableData((prev) =>
                  prev.map((el) => {
                    if (record.key !== el.key) return el;

                    const formatRecord: TableDataCharacteristic = {
                      ...el,
                      objectCharTypeId: selectId,
                      characteristic: findCharacteristic,
                      isNew: true,
                      // Очищаем поля зависимые от выбранной характеристики
                      specificDicId: undefined,
                      values: [],
                    };

                    if (newObjectChars && newObjectChars.length > 0) {
                      const findChar = newObjectChars.find(
                        (el) => el?.objectCharTypeId === formatRecord?.characteristic?.id
                      );

                      if (findChar?.values) {
                        const objectCharTypeValuesFormat = findChar.values.map((valEl) => ({
                          charTypeValue: {
                            id: Number(uniqueId()),
                            charTypeId: 0,
                            valueTypeId: 0,
                            unitTypeId: 0,
                            value: valEl.valDate || valEl.valDicId || valEl.valStr || valEl.valNum,
                            valueTo: "",
                            rangeTypeId: 0,
                          },
                          id: Number(uniqueId()),
                          objectCharTypeId: 0,
                          charTypeValueId: 0,
                        }));

                        const getValue = (value: CharacteristicValues) => {
                          return (
                            (value?.valDate || value?.valDicId || value?.valStr || value?.valNum) ??
                            null
                          );
                        };

                        const isDefaultFind = findChar.values.find((el) => el.isDefault);
                        // Если есть по умолчанию игнорируем правило подставления (если одно, то ставим сразу значение)
                        if (isDefaultFind) {
                          formatRecord.values = [
                            {
                              id: Number(uniqueId()),
                              value: getValue(isDefaultFind),
                            },
                          ];
                        } else if (findChar.values.length === 1) {
                          formatRecord.values = [
                            {
                              id: Number(uniqueId()),
                              value: getValue(findChar.values[0]),
                            },
                          ];
                        }

                        formatRecord.characteristic = {
                          ...formatRecord.characteristic,
                          objectCharTypeValues: objectCharTypeValuesFormat,
                        };
                      }
                    } else if (findCharacteristic?.objectCharTypeValues?.length === 1) {
                      // Если возможных значений одно, то сразу подставляем по дефолту
                      formatRecord.values = [
                        {
                          id: findCharacteristic.objectCharTypeValues[0].id,
                          value: findCharacteristic.objectCharTypeValues[0].charTypeValue.value,
                        },
                      ];
                    }

                    return formatRecord;
                  })
                );
              };

              return renderSelect(
                record,
                onSelectCharacteristic,
                !!isSelectedObjectId && !record.objectId
              );
            }
            if (!disabledValidation) {
              // Текст для обязательных характеристик
              if (record.isRequired) {
                const findCharacteristic = objectCharTypes.find((el) => el.id === value);
                return <RequiredMark>{findCharacteristic?.name}</RequiredMark>;
              }
              // Выпадающий список для необязательных
              const onSelectCharacteristic = (selectId: number) => {
                const findCharacteristic = objectCharTypes.find((el) => el.id === selectId);

                setTableData((prev) =>
                  prev.map((el) => {
                    if (record.key !== el.key) return el;

                    const formatRecord: TableDataCharacteristic = {
                      ...el,
                      objectCharTypeId: selectId,
                      characteristic: findCharacteristic,
                      isNew: true,
                      // Очищаем поля зависимые от выбранной характеристики
                      specificDicId: undefined,
                      values:
                        findCharacteristic?.objectCharTypeValues?.length === 1
                          ? [
                              {
                                id: findCharacteristic.objectCharTypeValues[0].id,
                                value:
                                  findCharacteristic.objectCharTypeValues[0].charTypeValue.value,
                              },
                            ]
                          : [],
                    };

                    if (findCharacteristic?.defaultValue) {
                      let value: CharacteristicValues["value"] = findCharacteristic.defaultValue;
                      let valDicId: number | undefined;
                      // const id = getId({
                      //   paramVal,
                      //   value,
                      //   objectCharTypeId: selectId,
                      // });
                      let id = Number(uniqueId());

                      if (
                        findCharacteristic.charType.dataTypeId ===
                        DataTypeIdValue["Словарный термин"]
                      ) {
                        const finDic = characteristicDictionaries?.find(
                          (dic) =>
                            dic?.dicType === findCharacteristic?.charType?.valueDicTypeAlias ||
                            dic?.data?.[0]?.dicTypeId ===
                              findCharacteristic?.charType?.valueDicTypeId
                        )?.data;

                        if (finDic) {
                          const findDicEl = finDic.find(
                            (dicEl) => String(dicEl.id) === String(findCharacteristic.defaultValue)
                          );
                          id = findDicEl ? Number(findDicEl.id) : Number(uniqueId());
                          value = Number(findDicEl?.id);
                          valDicId = Number(findDicEl?.id);
                        }
                      }

                      formatRecord.values = [{ id, value, valDicId }];
                    }

                    return formatRecord;
                  })
                );
              };

              return renderSelect(
                record,
                onSelectCharacteristic,
                (isSelectedObjectId && !record.objectId) ||
                  ((mode === "add" || mode === "edit") &&
                    record.characteristic?.accessModeId !== 2 &&
                    !record.isNew)
              );
            }
          }
          if (mode === "view") {
            return record?.characteristic?.name;
          }
        },
      },
      {
        key: "specificDicId",
        dataIndex: "specificDicId",
        title: "Параметры",
        hidden: false,
        width: columnsWidths.specificDicId,
        render(specificDicId, record) {
          // Ищем справочник который соответствует (id = specificDicId)
          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === record?.characteristic?.charType?.specificDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === record?.characteristic?.charType?.specificDicTypeId
          )?.data;

          if (disabledValidation) {
            if (mode === "create" || mode === "add" || mode === "edit") {
              // Выпадающий список, если charType.specificDicTypeId is not null
              if (record?.characteristic?.charType?.specificDicTypeId) {
                const onSelectSpecificDicId = (dicId) => {
                  setTableData((prev) => {
                    const newTableData = prev.map((el) => {
                      if (record.key === el.key) {
                        const formatRecord = {
                          ...el,
                          specificDicId: dicId,
                          values: [],
                        };

                        if (newObjectChars && newObjectChars?.length > 0) {
                          const findChar = newObjectChars?.find(
                            (el) => el?.specificDicId === dicId
                          );

                          formatRecord.characteristic = {
                            ...formatRecord.characteristic,
                            objectCharTypeValues: findChar?.values?.map((valEl) => ({
                              charTypeValue: {
                                id: Number(uniqueId()),
                                charTypeId: 0,
                                valueTypeId: 0,
                                unitTypeId: 0,
                                value:
                                  valEl.valDate || valEl.valDicId || valEl.valStr || valEl.valNum,
                                valueTo: "",
                                rangeTypeId: 0,
                              },
                              id: Number(uniqueId()),
                              objectCharTypeId: 0,
                              charTypeValueId: 0,
                            })),
                          };
                        }

                        return formatRecord;
                      }
                      return el;
                    });
                    return newTableData;
                  });
                };

                const getSpecificDicIdOptions = () => {
                  // Все характеристики которые совпадают с objectCharTypeId строки
                  const findAllObjectCharTypeId = tableData.filter(
                    (el) => el.objectCharTypeId === record.objectCharTypeId
                  );

                  return finDic
                    ?.filter((dicEl) => {
                      // Подставляем в возможные значения только то что приходит в newObjectChars
                      if (newObjectChars && newObjectChars?.length > 0) {
                        const filteredSpecificDicId: number[] = [];

                        // Оставляем только уникальные характеристики
                        const _ = newObjectChars?.forEach((el) => {
                          if (el?.objectCharTypeId === record?.objectCharTypeId) {
                            filteredSpecificDicId.push(Number(el.specificDicId));
                          }
                        });

                        const findChar = filteredSpecificDicId?.includes(Number(dicEl?.id));

                        return findChar;
                      }
                      return true;
                    })
                    ?.filter((el) => {
                      if (findAllObjectCharTypeId.length > 0) {
                        const chekSelect = findAllObjectCharTypeId.find(
                          (char) => char.key !== record.key && char.specificDicId === el.id
                        );

                        // Если такой параметр уже выбран, то не отображать его
                        if (chekSelect) {
                          return false;
                        }
                      }
                      return true;
                    });
                };

                return (
                  <Select
                    style={{ width: "100%" }}
                    allowClear={false}
                    value={specificDicId}
                    onChange={onSelectSpecificDicId}
                    className={`${
                      !specificDicId && fieldsError?.specificDicId
                        ? "characteristics-table__selectError"
                        : ""
                    }`}
                  >
                    {getSpecificDicIdOptions()?.map((el) => {
                      return (
                        <Select.Option key={el.id} value={el.id}>
                          {el.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                );
              }
              // Если charType.specificDicTypeId is NULL, список недоступен к выбору. Отображается  Нет доступных параметров
              return (
                <Select style={{ width: "100%" }} disabled placeholder="Нет доступных параметров" />
              );
            }

            if (mode === "view") {
              return (
                <Row justify="start">
                  {finDic?.find((el) => el.id === record.specificDicId)?.name || "-"}
                </Row>
              );
            }
          } else {
            if (mode === "create" || mode === "add" || mode === "edit") {
              // Выпадающий список, если charType.specificDicTypeId is not null
              if (record?.characteristic?.charType?.specificDicTypeId) {
                const onSelectSpecificDicId = (dicId) => {
                  setTableData((prev) => {
                    const newTableData = prev.map((el) => {
                      if (record.key === el.key) {
                        return {
                          ...el,
                          specificDicId: dicId,
                        };
                      }
                      return el;
                    });
                    return newTableData;
                  });
                };

                const getSpecificDicIdOptions = () => {
                  // Все характеристики которые совпадают с objectCharTypeId строки
                  const findAllObjectCharTypeId = tableData.filter(
                    (el) => el.objectCharTypeId === record.objectCharTypeId
                  );

                  return finDic?.filter((el) => {
                    if (findAllObjectCharTypeId.length > 0) {
                      const chekSelect = findAllObjectCharTypeId.find(
                        (char) => char.key !== record.key && char.specificDicId === el.id
                      );

                      // Если такой параметр уже выбран, то не отображать его
                      if (chekSelect) {
                        return false;
                      }
                    }
                    return true;
                  });
                };

                return (
                  <Select
                    style={{ width: "100%" }}
                    allowClear={false}
                    value={specificDicId}
                    onChange={onSelectSpecificDicId}
                    disabled={
                      (mode === "add" || mode === "edit") &&
                      record.characteristic?.accessModeId !== 2 &&
                      !record.isNew &&
                      !disabledValidation
                    }
                    className={`${
                      !specificDicId && fieldsError?.specificDicId
                        ? "characteristics-table__selectError"
                        : ""
                    }`}
                  >
                    {getSpecificDicIdOptions()?.map((el) => {
                      return (
                        <Select.Option key={el.id} value={el.id}>
                          {el.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                );
              }
              // Если charType.specificDicTypeId is NULL, список недоступен к выбору. Отображается  Нет доступных параметров
              return (
                <Select style={{ width: "100%" }} disabled placeholder="Нет доступных параметров" />
              );
            }

            if (mode === "view") {
              return (
                <Row justify="start">
                  {finDic?.find((el) => el.id === record.specificDicId)?.name || "-"}
                </Row>
              );
            }
          }

          return null;
        },
      },
      {
        key: "values",
        dataIndex: "values",
        title: valuesTitle || "Значение",
        hidden: false,
        width: columnsWidths.values,
        render(_, record) {
          // Поиск словаря
          const getFindDictionary = () => {
            return characteristicDictionaries?.find(
              (dic) =>
                dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                dic?.data?.[0]?.dicTypeId === record.characteristic?.charType?.valueDicTypeId
            )?.data;
          };

          const findDic = getFindDictionary();

          // Форматирование значения
          const formatValue = (
            item: CharacteristicValues,
            dictionary?: DictionaryDto<number>[]
          ) => {
            const getUnitTypeName = () => {
              // Поиск значения
              const getFindValue = (
                findId?: number,
                objectCharTypeValues?: IObjectCharTypeValue<string>[]
              ) => {
                return objectCharTypeValues?.find((el) => el.id === findId);
              };

              const findValue = getFindValue(item.id, record.characteristic?.objectCharTypeValues);

              if (findValue && findValue.charTypeValue.unitTypeId) {
                return (
                  versionsTableUtils.getDictionaryItemById(
                    characteristicsUnitsDic,
                    findValue.charTypeValue.unitTypeId
                  )?.name || ""
                );
              }

              return "";
            };

            if (
              record.characteristic?.charType?.dataTypeId === DataTypeIdValue["Словарный термин"]
            ) {
              return (
                dictionary?.find((dicItem) => dicItem.id === item.valDicId)?.name || item.value
              );
            }

            if (record.characteristic?.charType?.dataTypeId === DataTypeIdValue["Дата"]) {
              return `${toDisplayFormat(item.value)} ${getUnitTypeName()}`.trim();
            }

            return `${String(item.value)} ${getUnitTypeName()}`.trim();
          };

          const onChangeVisibleModalCharacteristic = (mode?: "view") => {
            if (record.characteristic) {
              if (isNewObjectChars) {
                // Для dataTypeId !== DataTypeIdValue["Словарный термин"]
                if (
                  Array.isArray(record.characteristic.objectCharTypeValues) &&
                  record.characteristic.objectCharTypeValues.length > 0
                ) {
                  // Ищем в жёстко заданных характеристиках характеристику строки
                  const findCharForNewObjectChars = newObjectChars?.find(
                    (newObjectCharsEl) =>
                      newObjectCharsEl?.objectCharType?.id === record?.objectCharTypeId
                  );

                  setCharacteristicValues(
                    record.characteristic.objectCharTypeValues
                      .filter((objectCharTypeValuesEl) => {
                        if (
                          findCharForNewObjectChars &&
                          Array.isArray(findCharForNewObjectChars?.values) &&
                          findCharForNewObjectChars?.values?.length > 0
                        ) {
                          const getFindFilter = (el: CharacteristicValues) => {
                            return (
                              el?.value === objectCharTypeValuesEl?.charTypeValue.value ||
                              el?.valDate === objectCharTypeValuesEl?.charTypeValue.value ||
                              (el?.valNum !== null &&
                                String(el?.valNum) ===
                                  String(objectCharTypeValuesEl?.charTypeValue.value)) ||
                              el?.valStr === objectCharTypeValuesEl?.charTypeValue.value
                            );
                          };

                          const findValue = findCharForNewObjectChars?.values?.find((el) =>
                            getFindFilter(el)
                          );

                          if (findValue === undefined) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map((el) => ({
                        id: el.id,
                        value: el.charTypeValue.value,
                      }))
                  );
                } else if (record?.values) {
                  setCharacteristicValues(
                    record?.values?.map((el) => ({
                      id: el.id,
                      value: el?.value,
                    }))
                  );
                }

                // Для dataTypeId === DataTypeIdValue["Словарный термин"]
                if (
                  record.characteristic?.charType?.dataTypeId ===
                  DataTypeIdValue["Словарный термин"]
                ) {
                  const finDic = characteristicDictionaries?.find(
                    (dic) =>
                      dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                      dic?.data?.[0]?.dicTypeId === record.characteristic?.charType?.valueDicTypeId
                  )?.data;

                  if (finDic) {
                    // Ищем в жёстко заданных характеристиках характеристику строки
                    const findDicFilter = finDic.filter((dicEl) => {
                      const findValue = record.characteristic?.objectCharTypeValues?.find(
                        (el) => Number(el?.charTypeValue.value) === dicEl?.id
                      );

                      if (findValue === undefined) {
                        return false;
                      }
                      return true;
                    });
                    console.log({ findDicFilter });

                    setCharacteristicValues(
                      findDicFilter?.map((el) => ({
                        id: el.id,
                        value: el.id,
                      }))
                    );
                  }
                }

                // При открытии указываем какое значение по умолчанию если оно есть
                const findIsDefault = record?.values?.find((el) => el?.isDefault);

                if (findIsDefault) {
                  setSelectedCharacteristicValueDefault([Number(findIsDefault.id)]);
                }

                setSelectedCharacteristicValues(
                  record?.values?.map((el) => {
                    if (
                      record.characteristic?.charType?.dataTypeId ===
                      DataTypeIdValue["Словарный термин"]
                    ) {
                      const finDic = characteristicDictionaries?.find(
                        (dic) =>
                          dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                          dic?.data?.[0]?.dicTypeId ===
                            record.characteristic?.charType?.valueDicTypeId
                      )?.data;

                      if (finDic) {
                        return Number(
                          finDic.find((dic) => dic.id === el.valDicId || dic.id === el.value)?.id
                        );
                      }
                    }

                    return Number(el.id);
                  }) || []
                );
                mode === "view"
                  ? setVisibleModalCharacteristicView({
                      ...record.characteristic,
                      key: record.key,
                      specificDicId: record.specificDicId,
                    })
                  : setVisibleModalCharacteristic({
                      ...record.characteristic,
                      key: record.key,
                    });
              }

              if (!isNewObjectChars) {
                // Для dataTypeId !== DataTypeIdValue["Словарный термин"]
                if (
                  Array.isArray(record.characteristic.objectCharTypeValues) &&
                  record.characteristic.objectCharTypeValues.length > 0
                ) {
                  if (mode === "view") {
                    setCharacteristicValues(
                      record?.values?.map((el) => ({
                        id: el.id,
                        value: el?.value,
                      })) || []
                    );
                  } else {
                    setCharacteristicValues(
                      record.characteristic.objectCharTypeValues.map((el) => ({
                        id: el.id,
                        value: el.charTypeValue.value,
                      }))
                    );
                  }
                } else if (record?.values) {
                  setCharacteristicValues(
                    record?.values?.map((el) => ({
                      id: el.id,
                      value: el?.value,
                    }))
                  );
                }

                // Для dataTypeId === DataTypeIdValue["Словарный термин"]
                if (
                  record.characteristic?.charType?.dataTypeId ===
                  DataTypeIdValue["Словарный термин"]
                ) {
                  const finDic = characteristicDictionaries?.find(
                    (dic) =>
                      dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                      dic?.data?.[0]?.dicTypeId === record.characteristic?.charType?.valueDicTypeId
                  )?.data;

                  if (finDic) {
                    setCharacteristicValues(
                      finDic.map((el) => ({
                        id: el.id,
                        value: el.id,
                      }))
                    );
                  }
                }

                // При открытии указываем какое значение по умолчанию если оно есть
                const findIsDefault = record?.values?.find((el) => el?.isDefault);

                if (findIsDefault) {
                  setSelectedCharacteristicValueDefault([
                    Number(findIsDefault.id) || Number(findIsDefault.value),
                  ]);
                }

                console.log({ findIsDefault, record });

                setSelectedCharacteristicValues(
                  record?.values?.map((el) => {
                    if (
                      record.characteristic?.charType?.dataTypeId ===
                      DataTypeIdValue["Словарный термин"]
                    ) {
                      const finDic = characteristicDictionaries?.find(
                        (dic) =>
                          dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                          dic?.data?.[0]?.dicTypeId ===
                            record.characteristic?.charType?.valueDicTypeId
                      )?.data;

                      if (finDic) {
                        return Number(
                          finDic.find((dic) => dic.id === el.valDicId || dic.id === el.value)?.id
                        );
                      }
                    }

                    return Number(el.id);
                  }) || []
                );
                mode === "view"
                  ? setVisibleModalCharacteristicView({ ...record.characteristic, key: record.key })
                  : setVisibleModalCharacteristic({ ...record.characteristic, key: record.key });
              }
            }
          };

          if (mode === "create" || mode === "add" || mode === "edit") {
            const getFields = () => {
              const isNotDisabledInputChange =
                record?.characteristic?.objectCharTypeValues?.length === 0 ||
                !record?.characteristic?.objectCharTypeValues?.length;

              if (
                !isNotDisabledInputChange ||
                record.characteristic?.charType?.dataTypeId ===
                  DataTypeIdValue["Словарный термин"] ||
                (mode !== "create" &&
                  record.characteristic?.accessModeId &&
                  record.characteristic?.accessModeId !== 2 &&
                  !record.isNew &&
                  !disabledValidation)
              ) {
                return (
                  <Select
                    value={record?.values?.map((el) => Number(el.id) || Number(el.value))}
                    disabled
                    showArrow={false}
                    mode="multiple"
                    className={`${
                      record?.values?.length === 0 && fieldsError?.values
                        ? "characteristics-table__selectError values_select_characteristic"
                        : "values_select_characteristic"
                    }`}
                    maxTagCount={3}
                    maxTagTextLength={10}
                  >
                    {record?.values?.map((item) => {
                      return (
                        <Select.Option
                          key={uniqueId()}
                          value={Number(item.id) || Number(item.value)}
                        >
                          {formatValue(item, findDic)}
                        </Select.Option>
                      );
                    })}
                  </Select>
                );
              }

              const onChangeValue = ({
                newValue,
              }: {
                newValue?: string | number | null | moment.Moment;
              }) => {
                setTableData((prev) => {
                  const newTableData = prev.map((el) => {
                    if (record?.key === el.key) {
                      return {
                        ...el,
                        values: [{ id: Number(uniqueId()), value: newValue }],
                      };
                    }
                    return el;
                  });
                  return newTableData;
                });
              };

              const isError =
                (record?.values?.length === 0 ||
                  (record.values?.length === 1 && !record.values?.[0].value)) &&
                fieldsError?.values;

              return getFieldForTypeCharacteristic(
                record.characteristic,
                record?.values?.[0]?.value,
                onChangeValue,
                undefined,
                isError
              );
            };

            const getThreeDots = () => {
              const divNull = <div style={{ width: 28 }} />;

              // Не показываем если нет значений и при этом не словарный термин
              const isNotVisibleTreeDots =
                (record?.characteristic?.objectCharTypeValues?.length === 0 ||
                  !record?.characteristic?.objectCharTypeValues?.length) &&
                record?.characteristic?.charType?.dataTypeId !==
                  DataTypeIdValue["Словарный термин"];

              if (isNotVisibleTreeDots) return divNull;

              const getCheckChange = () => {
                if (mode !== "create" && !disabledValidation && !record.isNew) {
                  if (
                    record.characteristic?.accessModeId &&
                    record.characteristic?.accessModeId === 2 &&
                    record.characteristic?.minCardinality &&
                    record.characteristic.minCardinality > 0
                  ) {
                    return false;
                  }

                  if (
                    (record.characteristic?.accessModeId &&
                      record.characteristic?.accessModeId !== 2) ||
                    (record.characteristic?.minCardinality &&
                      record.characteristic.minCardinality > 0)
                  ) {
                    return true;
                  }
                }
              };

              if (getCheckChange()) {
                return divNull;
              }

              if (record?.objectCharTypeId && record.characteristic?.charType?.dataTypeId) {
                // Если у характеристики тип словарный термин, то оббязательно сначала выбираем параметр для корректного расчёта кардинальности
                const isCheckDicRecordFail =
                  record?.characteristic?.charType?.specificDicTypeId && !record.specificDicId;

                return (
                  <Tooltip title={isCheckDicRecordFail ? "Выберите параметр" : ""}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: isCheckDicRecordFail ? "not-allowed" : "pointer",
                      }}
                    >
                      <ThreeDots
                        style={{
                          marginLeft: 6,
                          pointerEvents: isCheckDicRecordFail ? "none" : "auto",
                        }}
                        onClick={() => onChangeVisibleModalCharacteristic()}
                      />
                    </div>
                  </Tooltip>
                );
              }

              return divNull;
            };

            return (
              <Row wrap={false} justify="center" align="middle">
                {getFields()}
                {getThreeDots()}
              </Row>
            );
          }

          if (mode === "view") {
            const getSelect = () => {
              return (
                <Select
                  value={record?.values?.map((el) => Number(el.id) || Number(el.value))}
                  disabled
                  showArrow={false}
                  mode="multiple"
                  className={`${
                    record?.values?.length === 0 && fieldsError?.values
                      ? "characteristics-table__selectError values_select_characteristic"
                      : "values_select_characteristic"
                  }`}
                  maxTagCount={3}
                  maxTagTextLength={6}
                >
                  {record?.values?.map((item) => (
                    <Select.Option key={uniqueId()} value={Number(item.id) || Number(item.value)}>
                      {formatValue(item, findDic)}
                    </Select.Option>
                  ))}
                </Select>
              );
            };

            const getThreeDotsView = () => {
              return (
                <ThreeDots
                  style={{ cursor: "pointer", marginLeft: 6 }}
                  onClick={() => onChangeVisibleModalCharacteristic("view")}
                />
              );
            };

            return Array.isArray(record.values) && record.values?.length > 0 ? (
              <Row wrap={false} justify="center" align="middle">
                {getSelect()}
                {getThreeDotsView()}
              </Row>
            ) : null;
          }
          return null;
        },
      },
      {
        key: "defaultValue",
        dataIndex: "defaultValue",
        title: "Значение по умолчанию",
        hidden: !isInheritableObject || !!isDefaultDisabled,
        width: columnsWidths.defaultValue,
        render: (_, record) => {
          const findDefault = record?.values?.find((el) => el.isDefault);

          if (findDefault) {
            if (
              record.characteristic?.charType?.dataTypeId === DataTypeIdValue["Словарный термин"]
            ) {
              const finDic = characteristicDictionaries?.find(
                (dic) =>
                  dic?.dicType === record.characteristic?.charType?.valueDicTypeAlias ||
                  dic?.data?.[0]?.dicTypeId === record.characteristic?.charType?.valueDicTypeId
              )?.data;

              return finDic?.find((el) => el.id === findDefault.value)?.name;
            }
            if (record.characteristic?.charType?.dataTypeId === DataTypeIdValue["Дата"]) {
              return toDisplayFormat(findDefault.value);
            }
            return findDefault.value;
          }
          return null;
        },
      },
      {
        title: (params) => {
          // Отображаем если есть вообще характеристики и не в просмотре,
          // Или так же если isSelectedObjectId === true (поиск характеристик будет происходить прямо в таблице) и не в просмотре
          const isVisible =
            ((findObjectCharTypes?.length > 0 || isSelectedObjectId) && mode !== "view") ||
            (isNewObjectChars && mode !== "view");

          // Добавляем новую строку с характеристикой в таблицу
          const onAddRow = () => {
            setTableData((prev) => {
              const newRow = {
                key: `id=${0}_unicKey=${uniqueId()}`,
                objectId: !isSelectedObjectId && isInheritableObject ? objectTypeId : undefined,
                isNew: true,
              };

              return [...prev, newRow];
            });
          };

          // Проверяем есть ли ещё характеристики доступные к выбору
          const lengthCharacteristics = getOptionsCharacteristicsLength();

          // Проверяем есть ли строка без выбранной характеристики
          const findNotValueChar = tableData.find((el) => !el.objectCharTypeId);
          return (
            <>
              {isVisible &&
              lengthCharacteristics &&
              lengthCharacteristics > 0 &&
              !findNotValueChar ? (
                <Row justify="end" wrap={false} style={{ paddingRight: 3 }}>
                  <Tooltip placement="topRight" title="Добавить характеристику">
                    <PlusIcon
                      onClick={() => onAddRow()}
                      className="characteristics-table__addButton"
                    />
                  </Tooltip>
                </Row>
              ) : null}
            </>
          );
        },
        dataIndex: "btns",
        key: "btns",
        width: "42px",
        render: (_, record) => {
          const onDelete = () => {
            setTableData((prev) => {
              setSelectedCharacteristicValueDefault([]);
              return prev.filter((el) => el?.key !== record?.key);
            });
          };

          if (disabledValidation) {
            if (mode === "create" || mode === "add" || mode === "edit") {
              return (
                <Row justify="end" wrap={false}>
                  <DeleteIcon className="characteristics-table__deleteBtn" onClick={onDelete} />
                </Row>
              );
            }
          } else {
            if (record.isRequired) {
              return null;
            }

            // accessMode = 2 Изменяемый, другие не изменяемые
            if (
              (mode === "add" || mode === "edit") &&
              record.characteristic?.accessModeId !== 2 &&
              !record.isNew
            ) {
              return null;
            }

            // Кнопки в режиме создания, добавления и редактирования
            if (mode === "create" || mode === "add" || mode === "edit") {
              return (
                <Row justify="end" wrap={false}>
                  <DeleteIcon className="characteristics-table__deleteBtn" onClick={onDelete} />
                </Row>
              );
            }
          }

          return null;
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<TableDataCharacteristic>[]>(getColumns());

  useEffect(() => {
    setColumns(getColumns());
  }, [
    findObjectCharTypes,
    newObjectChars,
    isInheritableObject,
    tableData,
    characteristicDictionaries,
    objectTypes,
    objectCharTypes,
    objectTypeId,
    isNewObjectChars,
    isSelectedObjectId,
    mode,
    fieldsError,
    fromDateCharacteristic,
  ]);

  // Для правильного окончания слова
  const getWordForm = (number) => {
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return "значений";
    if (lastDigit === 1) return "значение";
    if (lastDigit >= 2 && lastDigit <= 4) return "значения";
    return "значений";
  };

  const [searchValueModal, setSearchValueModal] = useState("");

  const modalCharacteristicValues = () => {
    const onCloseModal = () => {
      setVisibleModalCharacteristic(undefined);
      setCharacteristicValues([{ id: Number(uniqueId()) }]);
      setSelectedCharacteristicValues([]);
      setSearchValueModal("");
    };
    const onApplyModal = () => {
      const onCheckCardinality = () => {
        const findRecordCurrent = tableData.find(
          (tableEl) => tableEl.key === visibleModalCharacteristic?.key
        );

        // Поиск характеристик которые совпадают с objectCharTypeId характеристики в модалке
        // И всех значений выбранных в них
        const findAllObjectCharTypeIdValuesLength = tableData
          .filter((tableEl) => {
            // Для характеристик которые имеют specificDicTypeId можно на каждый параметр создавать значения
            if (visibleModalCharacteristic?.charType?.specificDicTypeId) {
              return (
                visibleModalCharacteristic?.charTypeId &&
                tableEl.characteristic?.charTypeId &&
                visibleModalCharacteristic?.charTypeId === tableEl.characteristic?.charTypeId &&
                visibleModalCharacteristic.key !== tableEl.key &&
                findRecordCurrent?.specificDicId === tableEl.specificDicId
              );
            }
            return (
              visibleModalCharacteristic?.charTypeId &&
              tableEl.characteristic?.charTypeId &&
              visibleModalCharacteristic?.charTypeId === tableEl.characteristic?.charTypeId &&
              visibleModalCharacteristic.key !== tableEl.key
            );
          })
          .flatMap((el) => el.values).length;

        if (visibleModalCharacteristic?.maxCardinality) {
          if (
            findAllObjectCharTypeIdValuesLength + selectedCharacteristicValues.length >
            visibleModalCharacteristic?.maxCardinality
          ) {
            return {
              isValidCardinality: false,
              allCount: findAllObjectCharTypeIdValuesLength + selectedCharacteristicValues.length,
              maxCount: visibleModalCharacteristic?.maxCardinality,
              minCount: visibleModalCharacteristic?.minCardinality,
            };
          }
        }
        if (visibleModalCharacteristic?.minCardinality) {
          if (
            findAllObjectCharTypeIdValuesLength + selectedCharacteristicValues.length <
            visibleModalCharacteristic?.minCardinality
          ) {
            return {
              isValidCardinality: false,
              allCount: findAllObjectCharTypeIdValuesLength + selectedCharacteristicValues.length,
              maxCount: visibleModalCharacteristic?.maxCardinality,
              minCount: visibleModalCharacteristic?.minCardinality,
            };
          }
        }

        return { isValidCardinality: true };
      };

      const validCardinality = onCheckCardinality();

      if (!validCardinality.isValidCardinality && !disabledValidation) {
        const getInfoCardinality = () => {
          const { minCount, maxCount } = validCardinality || {};

          if (minCount && maxCount) {
            if (minCount === maxCount) {
              return `Укажите ${minCount} ${getWordForm(minCount)}`;
            }
            return `Для характеристики возможно указать от ${minCount} до ${maxCount} значений.`;
          }
          if (maxCount && !minCount) {
            return ` Для характеристики возможно указать до ${maxCount} значений.`;
          }
          if (!maxCount && minCount) {
            return `Для характеристики возможно указать от ${minCount} значений.`;
          }
        };

        Modal.info({
          icon: false,
          content: (
            <Row justify="center" gutter={[30, 30]}>
              <Col span={24} style={{ textAlign: "center" }}>
                {getInfoCardinality()}
              </Col>
              <Col span={24} style={{ textAlign: "center" }}>
                Указано {validCardinality.allCount} {getWordForm(validCardinality.allCount)}
              </Col>
            </Row>
          ),
          bodyStyle: { display: "flex", justifyContent: "center" },
          okButtonProps: { style: { width: "99px" } },
          okText: "Ok",
        });
        return;
      }

      setTableData((prev) => {
        const newTableData = prev.map((el) => {
          if (visibleModalCharacteristic?.key === el.key) {
            return {
              ...el,
              values: characteristicValues?.flatMap((el) => {
                if (!selectedCharacteristicValues.includes(Number(el.id))) {
                  return [];
                }
                return {
                  id: el.id,
                  isDefault:
                    el?.isDefault ||
                    selectedCharacteristicValueDefault.includes(Number(el.id)) ||
                    undefined,
                  value: el?.value,
                  valDate: el?.valDate,
                  valDicId:
                    visibleModalCharacteristic?.charType?.dataTypeId ===
                    DataTypeIdValue["Словарный термин"]
                      ? el?.id
                      : el?.valDicId,
                  valNum: el?.valNum,
                  valStr: el?.valStr,
                };
              }),
            };
          }
          return el;
        });

        return newTableData;
      });

      onCloseModal();
    };
    const onChageValue = ({
      newValue,
      searchId,
    }: {
      newValue?: string | number | null | moment.Moment;
      searchId?: number;
    }) => {
      setCharacteristicValues((prev) => {
        return prev.map((el) => {
          if (el.id === searchId) {
            return { ...el, value: newValue };
          }
          return el;
        });
      });
    };

    // Отображаем минимальную и максимальную кардинальность или null
    const getStingCardinality = () => {
      let strCardinality = "";

      const { minCardinality, maxCardinality } = visibleModalCharacteristic || {};

      if (minCardinality && maxCardinality) {
        if (minCardinality === maxCardinality) {
          strCardinality = `Выберите ${minCardinality} ${getWordForm(minCardinality)}`;
        } else {
          strCardinality = `Выберите от ${minCardinality} до ${maxCardinality} значений`;
        }
      } else if (minCardinality && !maxCardinality) {
        strCardinality = `Выберите от ${minCardinality} значений`;
      } else if (!minCardinality && maxCardinality) {
        strCardinality = `Выберите до ${maxCardinality} значений`;
      }

      return strCardinality;
    };

    // console.log({
    //   selectedCharacteristicValues,
    //   visibleModalCharacteristic,
    //   visibleModalCharacteristicView,
    //   searchValueModal,
    //   characteristicValues,
    // });

    // Для типов dataTypeId === 4
    if (visibleModalCharacteristic?.charType?.dataTypeId === 4) {
      const getFindDic = () => {
        if (isNewObjectChars) {
          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === visibleModalCharacteristic?.charType?.valueDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === visibleModalCharacteristic?.charType?.valueDicTypeId
          )?.data;

          return finDic;
        }
        if (!isNewObjectChars) {
          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === visibleModalCharacteristic?.charType?.valueDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === visibleModalCharacteristic?.charType?.valueDicTypeId
          )?.data;
          return finDic;
        }
      };

      // Для наследуемых клик на Radio для установки элемента по умолчанию
      const onClickDefault = (id) => {
        if (selectedCharacteristicValueDefault.includes(Number(id))) {
          setSelectedCharacteristicValueDefault([]);
        } else {
          setSelectedCharacteristicValueDefault([Number(id)]);
        }
      };

      // Отображаем строки в модалке
      const getListForCharacteristicValues = () => {
        const onChecked = (e: CheckboxChangeEvent) => {
          setSelectedCharacteristicValues((prev) => {
            if (prev.includes(e.target.value)) {
              if (Number(e.target.value) === selectedCharacteristicValueDefault[0]) {
                setSelectedCharacteristicValueDefault([]);
              }
              return prev.filter((el) => el !== e.target.value);
            }
            if (characteristicValues.length === 1) {
              setSelectedCharacteristicValueDefault([Number(e.target.value)]);
            }
            return prev.concat(e.target.value);
          });
        };
        const onCheckedAll = (e: CheckboxChangeEvent) => {
          setSelectedCharacteristicValues(() => {
            if (selectedCharacteristicValues.length === characteristicValues?.length) {
              setSelectedCharacteristicValueDefault([]);
              return [];
            }
            if (visibleModalCharacteristic?.objectCharTypeValues) {
              setSelectedCharacteristicValues(characteristicValues?.map((el) => Number(el.id)));
            }
            return [];
          });
        };

        const findDic = getFindDic();

        const checkedAll = isNewObjectChars
          ? selectedCharacteristicValues?.length ===
            visibleModalCharacteristic?.objectCharTypeValues?.length
          : selectedCharacteristicValues?.length === findDic?.length;

        return (
          <>
            <Checkbox checked={checkedAll} onChange={onCheckedAll}>
              Выбрать все
            </Checkbox>
            {characteristicValues?.length > 0
              ? characteristicValues
                  ?.filter(
                    (el) =>
                      String(el?.value)
                        .toLowerCase()
                        .includes(String(searchValueModal).toLowerCase()) ||
                      findDic?.find(
                        (dicEl) =>
                          dicEl?.id === el?.id &&
                          String(dicEl?.name)
                            .toLowerCase()
                            .includes(String(searchValueModal).toLowerCase())
                      )
                  )
                  ?.map((el) => {
                    return (
                      <Row className="characteristic-list-item" key={el?.id}>
                        {characteristicValues?.length === 1 ? (
                          <Col span={isInheritableObject ? 21 : 23}>
                            <Checkbox
                              value={el?.id}
                              checked={selectedCharacteristicValues.includes(Number(el?.id))}
                              onChange={(e) => onChecked(e)}
                              style={{ marginRight: 16 }}
                            />
                            {findDic?.find((dic) => dic.id === el?.value)?.name}
                          </Col>
                        ) : (
                          <>
                            <Col span={isInheritableObject ? 19 : 21}>
                              <Checkbox
                                value={el?.id}
                                checked={selectedCharacteristicValues.includes(Number(el?.id))}
                                onChange={(e) => onChecked(e)}
                                style={{ marginRight: 16 }}
                              />
                              {findDic?.find((dic) => dic.id === el?.value)?.name}
                            </Col>
                            <Col span={isInheritableObject ? 5 : 3}>
                              <Row justify="end" wrap={false} style={{ marginLeft: 10 }}>
                                {isInheritableObject && !isDefaultDisabled ? (
                                  <Tooltip title="По умолчанию" mouseLeaveDelay={0}>
                                    <Radio
                                      checked={selectedCharacteristicValueDefault.includes(
                                        Number(el?.id)
                                      )}
                                      disabled={
                                        !selectedCharacteristicValues.includes(Number(el?.id))
                                      }
                                      onClick={() => {
                                        onClickDefault(el?.id);
                                      }}
                                      value={el?.id}
                                      style={{ marginLeft: 10, paddingTop: 2 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })
              : null}
          </>
        );
      };

      return (
        <ModalWindow
          visible={visibleModalCharacteristic}
          title={visibleModalCharacteristic?.name}
          handleSubmit={onApplyModal}
          handleCancel={onCloseModal}
          handleReset={onCloseModal}
          cancelTextButton="Отмена"
          submitTextButton="Сохранить"
          width={400}
          disabled={false}
          className="characteristic-modal"
        >
          <Row className="characteristic-list-container">
            <Col span={24}>
              <Row>
                {!disabledValidation && (
                  <Col span={24} style={{ marginBottom: 6, color: "#8899AA" }}>
                    {getStingCardinality()}
                  </Col>
                )}
                <Col span={24} style={{ marginBottom: 15 }}>
                  <Input
                    value={searchValueModal}
                    onChange={(e) => {
                      setSearchValueModal(e.target.value);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
              {getListForCharacteristicValues()}
            </Col>
          </Row>
        </ModalWindow>
      );
    }

    // Для всех типов кроме dataTypeId === 4
    if (visibleModalCharacteristic?.charType?.dataTypeId !== 4) {
      // Удалить строку из модалки
      const onDelete = (id) => {
        setCharacteristicValues((prev) => prev.filter((el) => el.id !== id));
      };

      // Добавить строку по нажатию на Enter
      const onAddRow = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
          setCharacteristicValues((prev) => prev.concat({ id: Number(uniqueId()) }));
        }
      };

      // Для наследуемых клик на Radio для установки элемента по умолчанию
      const onClickDefault = (id) => {
        if (selectedCharacteristicValueDefault.includes(Number(id))) {
          setSelectedCharacteristicValueDefault([]);
        } else {
          setSelectedCharacteristicValueDefault([Number(id)]);
        }
      };

      // Отображаем строки в модалке доступные для редактирования (если нет visibleModalCharacteristic.objectCharTypeValues)
      const getListForCharacteristicNotValues = () => {
        return (
          <>
            {characteristicValues.length > 0
              ? characteristicValues
                  ?.filter((el) => {
                    if (
                      visibleModalCharacteristicView?.charType?.dataTypeId ===
                      DataTypeIdValue["Дата"]
                    ) {
                      return String(toDisplayFormat(el.value)).includes(searchValueModal);
                    }
                    return String(el.value)
                      .toLowerCase()
                      .includes(String(searchValueModal).toLowerCase());
                  })
                  .map((el) => {
                    return (
                      <Row className="characteristic-list-item" key={uniqueId()}>
                        {characteristicValues.length === 1 ? (
                          <Col span={isInheritableObject ? 21 : 23} onKeyDown={(e) => onAddRow(e)}>
                            {getFieldForTypeCharacteristic(
                              visibleModalCharacteristic,
                              el.value,
                              onChageValue,
                              el.id
                            )}
                          </Col>
                        ) : (
                          <>
                            <Col
                              span={isInheritableObject ? 19 : 21}
                              onKeyDown={(e) => onAddRow(e)}
                            >
                              {getFieldForTypeCharacteristic(
                                visibleModalCharacteristic,
                                el.value,
                                onChageValue,
                                el.id
                              )}
                            </Col>
                            <Col span={isInheritableObject ? 5 : 3}>
                              <Row justify="end" wrap={false} style={{ marginLeft: 10 }}>
                                {characteristicValues.length !== 1 && (
                                  <DeleteIcon
                                    className="characteristics-table__deleteBtn"
                                    onClick={() => onDelete(el.id)}
                                  />
                                )}
                                {isInheritableObject && !isDefaultDisabled ? (
                                  <Tooltip title="По умолчанию" mouseLeaveDelay={0}>
                                    <Radio
                                      checked={selectedCharacteristicValueDefault.includes(
                                        Number(el.id)
                                      )}
                                      disabled={
                                        !selectedCharacteristicValues.includes(Number(el.id))
                                      }
                                      onClick={() => {
                                        onClickDefault(el.id);
                                      }}
                                      value={el.id}
                                      style={{ marginLeft: 10, paddingTop: 2 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })
              : null}
          </>
        );
      };

      // Отображаем строки в модалке не доступные для редактирования (если есть visibleModalCharacteristic.objectCharTypeValues)
      const getListForCharacteristicInqludesValues = () => {
        const onChecked = (e: CheckboxChangeEvent) => {
          setSelectedCharacteristicValues((prev) => {
            if (prev.includes(e.target.value)) {
              if (Number(e.target.value) === selectedCharacteristicValueDefault[0]) {
                setSelectedCharacteristicValueDefault([]);
              }
              return prev.filter((el) => el !== e.target.value);
            }
            if (characteristicValues.length === 1) {
              setSelectedCharacteristicValueDefault([Number(e.target.value)]);
            }
            return prev.concat(e.target.value);
          });
        };
        const onCheckedAll = () => {
          setSelectedCharacteristicValues(() => {
            if (selectedCharacteristicValues.length === characteristicValues?.length) {
              setSelectedCharacteristicValueDefault([]);
              return [];
            }
            if (visibleModalCharacteristic?.objectCharTypeValues) {
              setSelectedCharacteristicValues(characteristicValues?.map((el) => Number(el.id)));
            }
            return [];
          });
        };

        return (
          <>
            <Checkbox
              checked={selectedCharacteristicValues.length === characteristicValues?.length}
              onChange={onCheckedAll}
            >
              Выбрать все
            </Checkbox>
            {characteristicValues.length > 0
              ? characteristicValues
                  ?.filter((el) => {
                    if (
                      visibleModalCharacteristicView?.charType?.dataTypeId ===
                      DataTypeIdValue["Дата"]
                    ) {
                      return String(toDisplayFormat(el.value)).includes(searchValueModal);
                    }
                    return String(el.value)
                      .toLowerCase()
                      .includes(String(searchValueModal).toLowerCase());
                  })
                  .map((el) => {
                    const formatValue = (item: CharacteristicValues) => {
                      const getUnitTypeName = () => {
                        // Поиск значения
                        const getFindValue = (
                          findId?: number,
                          objectCharTypeValues?: IObjectCharTypeValue<string>[]
                        ) => {
                          return objectCharTypeValues?.find((el) => el.id === findId);
                        };

                        const findValue = getFindValue(
                          item.id,
                          visibleModalCharacteristic?.objectCharTypeValues
                        );

                        if (findValue && findValue.charTypeValue.unitTypeId) {
                          return (
                            versionsTableUtils.getDictionaryItemById(
                              characteristicsUnitsDic,
                              findValue.charTypeValue.unitTypeId
                            )?.name || ""
                          );
                        }

                        return "";
                      };

                      if (
                        visibleModalCharacteristic?.charType?.dataTypeId === DataTypeIdValue["Дата"]
                      ) {
                        return `${toDisplayFormat(item.value)} ${getUnitTypeName()}`.trim();
                      }

                      return `${String(item.value)} ${getUnitTypeName()}`.trim();
                    };

                    const value = formatValue(el);

                    return (
                      <Row className="characteristic-list-item" key={el.id}>
                        {characteristicValues.length === 1 ? (
                          <Col span={isInheritableObject ? 21 : 23} onKeyDown={(e) => onAddRow(e)}>
                            <Checkbox
                              value={el.id}
                              checked={selectedCharacteristicValues.includes(Number(el.id))}
                              onChange={(e) => onChecked(e)}
                              style={{ marginRight: 16 }}
                            />
                            {value}
                          </Col>
                        ) : (
                          <>
                            <Col span={isInheritableObject ? 19 : 21}>
                              <Checkbox
                                value={el.id}
                                checked={selectedCharacteristicValues.includes(Number(el.id))}
                                onChange={(e) => onChecked(e)}
                                style={{ marginRight: 16 }}
                              />
                              {value}
                            </Col>
                            <Col span={isInheritableObject ? 5 : 3}>
                              <Row justify="end" wrap={false} style={{ marginLeft: 10 }}>
                                {isInheritableObject && !isDefaultDisabled ? (
                                  <Tooltip title="По умолчанию" mouseLeaveDelay={0}>
                                    <Radio
                                      checked={selectedCharacteristicValueDefault.includes(
                                        Number(el.id)
                                      )}
                                      disabled={
                                        !selectedCharacteristicValues.includes(Number(el.id))
                                      }
                                      onClick={() => {
                                        onClickDefault(el.id);
                                      }}
                                      value={el.id}
                                      style={{ marginLeft: 10, paddingTop: 2 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })
              : null}
          </>
        );
      };

      return (
        <ModalWindow
          visible={visibleModalCharacteristic}
          title={visibleModalCharacteristic?.name}
          handleSubmit={onApplyModal}
          handleCancel={onCloseModal}
          handleReset={onCloseModal}
          cancelTextButton="Отмена"
          submitTextButton="Сохранить"
          width={400}
          disabled={false}
          className="characteristic-modal"
        >
          <Row className="characteristic-list-container">
            <Col span={24}>
              <Row>
                {!disabledValidation && (
                  <Col span={24} style={{ marginBottom: 6, color: "#8899AA" }}>
                    {getStingCardinality()}
                  </Col>
                )}
                <Col span={24} style={{ marginBottom: 15 }}>
                  <Input
                    value={searchValueModal}
                    onChange={(e) => {
                      setSearchValueModal(e.target.value);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
              {!visibleModalCharacteristic?.objectCharTypeValues ||
              visibleModalCharacteristic?.objectCharTypeValues?.length === 0
                ? getListForCharacteristicNotValues()
                : getListForCharacteristicInqludesValues()}
            </Col>
          </Row>
        </ModalWindow>
      );
    }

    return null;
  };

  const modalViewCharacteristicValues = () => {
    const onCloseModal = () => {
      setVisibleModalCharacteristicView(undefined);
      setCharacteristicValues([{ id: Number(uniqueId()) }]);
      setSelectedCharacteristicValues([]);
      setSearchValueModal("");
    };

    // Для типов dataTypeId === 4
    if (visibleModalCharacteristicView?.charType?.dataTypeId === 4) {
      const getFindDic = () => {
        if (isNewObjectChars) {
          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === visibleModalCharacteristicView?.charType?.valueDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === visibleModalCharacteristicView?.charType?.valueDicTypeId
          )?.data;

          const findCharForNewObjectChars = newObjectChars?.find(
            (newObjectCharsEl) =>
              newObjectCharsEl?.objectCharType?.id === visibleModalCharacteristicView?.id &&
              visibleModalCharacteristicView?.specificDicId === newObjectCharsEl?.specificDicId
          );

          const findDicFilter = finDic?.filter((dicEl) => {
            if (
              findCharForNewObjectChars &&
              Array.isArray(findCharForNewObjectChars?.values) &&
              findCharForNewObjectChars?.values?.length > 0
            ) {
              const findValue = findCharForNewObjectChars?.values?.find(
                (el) => el?.valDicId === dicEl?.id
              );

              if (findValue === undefined) {
                return false;
              }
            }
            return true;
          });

          return findDicFilter;
        }
        if (!isNewObjectChars) {
          const finDic = characteristicDictionaries?.find(
            (dic) =>
              dic?.dicType === visibleModalCharacteristicView?.charType?.valueDicTypeAlias ||
              dic?.data?.[0]?.dicTypeId === visibleModalCharacteristicView?.charType?.valueDicTypeId
          )?.data;
          return finDic;
        }
      };

      // Отображаем строки в модалке
      const getListForCharacteristicValues = () => {
        const findDic = getFindDic();
        return (
          <>
            {characteristicValues?.length > 0
              ? characteristicValues
                  ?.filter(
                    (el) =>
                      String(el?.value)
                        .toLowerCase()
                        .includes(String(searchValueModal).toLowerCase()) ||
                      findDic?.find(
                        (dicEl) =>
                          dicEl?.id === el?.id &&
                          String(dicEl?.name)
                            .toLowerCase()
                            .includes(String(searchValueModal).toLowerCase())
                      )
                  )
                  ?.filter((el) => selectedCharacteristicValues.includes(Number(el?.id)))
                  ?.map((el) => {
                    return (
                      <Row className="characteristic-list-item" key={el?.id}>
                        {characteristicValues?.length === 1 ? (
                          <Col span={isInheritableObject ? 21 : 23}>
                            {findDic?.find((dic) => dic.id === el?.value)?.name}
                          </Col>
                        ) : (
                          <>
                            <Col span={isInheritableObject ? 19 : 21}>
                              {findDic?.find((dic) => dic.id === el?.value)?.name}
                            </Col>
                            <Col span={isInheritableObject ? 5 : 3}>
                              <Row justify="end" wrap={false} style={{ marginLeft: 10 }}>
                                {isInheritableObject &&
                                !isDefaultDisabled &&
                                selectedCharacteristicValueDefault.includes(Number(el?.id)) ? (
                                  <Tooltip title="По умолчанию" mouseLeaveDelay={0}>
                                    <Radio
                                      checked
                                      disabled
                                      value={el?.id}
                                      style={{ marginLeft: 10, paddingTop: 2 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })
              : null}
          </>
        );
      };

      return (
        <ModalWindow
          visible={visibleModalCharacteristicView}
          title={visibleModalCharacteristicView?.name}
          handleSubmit={() => {}}
          handleCancel={onCloseModal}
          handleReset={onCloseModal}
          cancelTextButton="Отмена"
          submitTextButton="Сохранить"
          width={400}
          disabled={false}
          className="characteristic-modal"
          footerHide
        >
          <Row className="characteristic-list-container">
            <Col span={24}>
              <Row>
                <Col span={24}>
                  <Input
                    value={searchValueModal}
                    onChange={(e) => {
                      setSearchValueModal(e.target.value);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
              {getListForCharacteristicValues()}
            </Col>
          </Row>
        </ModalWindow>
      );
    }

    // Для всех типов кроме dataTypeId === 4
    if (visibleModalCharacteristicView?.charType?.dataTypeId !== 4) {
      // Отображаем строки в модалке не доступные для редактирования (если есть visibleModalCharacteristicView.objectCharTypeValues)
      const getListForCharacteristicInqludesValues = () => {
        // console.log({
        //   characteristicValues,
        //   searchValueModal,
        //   selectedCharacteristicValues,
        // });

        return (
          <>
            {characteristicValues.length
              ? characteristicValues
                  .filter((el) => {
                    if (
                      visibleModalCharacteristicView?.charType?.dataTypeId ===
                      DataTypeIdValue["Дата"]
                    ) {
                      return String(toDisplayFormat(el.value)).includes(searchValueModal);
                    }
                    return String(el.value)
                      .toLowerCase()
                      .includes(String(searchValueModal).toLowerCase());
                  })
                  ?.filter((el) => selectedCharacteristicValues.includes(Number(el?.id)))
                  .map((el) => {
                    // Форматирование значения
                    const formatValue = (item: CharacteristicValues) => {
                      const getUnitTypeName = () => {
                        // Поиск значения
                        const getFindValue = (
                          findId?: number,
                          objectCharTypeValues?: IObjectCharTypeValue<string>[]
                        ) => {
                          return objectCharTypeValues?.find((el) => el.id === findId);
                        };

                        const findValue = getFindValue(
                          item.id,
                          visibleModalCharacteristicView?.objectCharTypeValues
                        );

                        if (findValue && findValue.charTypeValue.unitTypeId) {
                          return (
                            versionsTableUtils.getDictionaryItemById(
                              characteristicsUnitsDic,
                              findValue.charTypeValue.unitTypeId
                            )?.name || ""
                          );
                        }

                        return "";
                      };

                      if (
                        visibleModalCharacteristicView?.charType?.dataTypeId ===
                        DataTypeIdValue["Дата"]
                      ) {
                        return `${toDisplayFormat(item.value)} ${getUnitTypeName()}`.trim();
                      }

                      return `${String(item.value)} ${getUnitTypeName()}`.trim();
                    };

                    const value = formatValue(el);

                    return (
                      <Row className="characteristic-list-item" key={el.id}>
                        {characteristicValues.length === 1 ? (
                          <Col span={isInheritableObject ? 21 : 23}>{value}</Col>
                        ) : (
                          <>
                            <Col span={isInheritableObject ? 19 : 21}>{value}</Col>
                            <Col span={isInheritableObject ? 5 : 3}>
                              <Row justify="end" wrap={false} style={{ marginLeft: 10 }}>
                                {isInheritableObject &&
                                !isDefaultDisabled &&
                                selectedCharacteristicValueDefault.includes(Number(el?.id)) ? (
                                  <Tooltip title="По умолчанию" mouseLeaveDelay={0}>
                                    <Radio
                                      checked
                                      disabled
                                      value={el.id}
                                      style={{ marginLeft: 10, paddingTop: 2 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })
              : null}
          </>
        );
      };

      return (
        <ModalWindow
          visible={visibleModalCharacteristicView}
          title={visibleModalCharacteristicView?.name}
          handleSubmit={() => {}}
          handleCancel={onCloseModal}
          handleReset={onCloseModal}
          cancelTextButton="Отмена"
          submitTextButton="Сохранить"
          width={400}
          disabled={false}
          className="characteristic-modal"
          footerHide
        >
          <Row className="characteristic-list-container">
            <Col span={24}>
              <Row>
                <Col span={24}>
                  <Input
                    value={searchValueModal}
                    onChange={(e) => {
                      setSearchValueModal(e.target.value);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
              {getListForCharacteristicInqludesValues()}
            </Col>
          </Row>
        </ModalWindow>
      );
    }

    return null;
  };

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableRef.current) {
      // Находим таблицы в header и body
      const headerTable = tableRef.current.querySelector(
        ".ant-table-container-characteristic .ant-table-header table"
      );
      const bodyTable = tableRef.current.querySelector(
        ".ant-table-container-characteristic .ant-table-body table"
      );

      if (!headerTable || !bodyTable) return;

      // Копируем colgroup из body в header
      const bodyColGroup = bodyTable.querySelector("colgroup");
      if (bodyColGroup) {
        // Удаляем старый colgroup в header если есть
        const existingHeaderColGroup = headerTable.querySelector("colgroup");
        if (existingHeaderColGroup) {
          headerTable.removeChild(existingHeaderColGroup);
        }

        // Клонируем и вставляем
        headerTable.insertBefore(bodyColGroup.cloneNode(true), headerTable.firstChild);
      }
    }
  }, [tableRef.current]);

  return (
    <div className="characteristics-table">
      {isChildCharacteristic ? null : <Divider style={{ margin: "0 0 9px" }} />}

      <div ref={tableRef} className="ant-table-container-characteristic">
        <Table
          dataSource={tableData}
          columns={columns.filter(({ hidden }) => !hidden)}
          loading={checkIsLoading(
            objectTypesLoading,
            objectCharTypesLoading,
            characteristicDictionariesLoading
          )}
          pagination={false}
          size="middle"
          scroll={{
            y: 285,
          }}
        />
      </div>

      {modalCharacteristicValues()}
      {modalViewCharacteristicValues()}
    </div>
  );
};
